import { atticActivityListPath } from "@dashboard/atticActivity/urls";
import { atticAssetDirectoryListPath } from "@dashboard/atticAssets/urls";
import { atticStockLookupPath } from "@dashboard/atticInventory/urls";
import { useUser } from "@dashboard/auth/useUser";
import { categoryListUrl } from "@dashboard/categories/urls";
import { collectionListUrl } from "@dashboard/collections/urls";
import { navigationLucideIconProps } from "@dashboard/components/icons";
import { useAnalytics } from "@dashboard/components/ProductAnalytics/useAnalytics";
import { configurationMenuUrl } from "@dashboard/configuration/urls";
import { getConfigMenuItemsPermissions } from "@dashboard/configuration/utils";
import { rippleNewCustomersView } from "@dashboard/customers/ripples/newCustomersView";
import { customerListUrl } from "@dashboard/customers/urls";
import { customerTypeListUrl } from "@dashboard/customerTypes/urls";
import { saleListUrl, voucherListUrl } from "@dashboard/discounts/urls";
import { SidebarAppAlert } from "@dashboard/extensions/components/AppAlerts/SidebarAppAlert";
import { useAppsAlert } from "@dashboard/extensions/components/AppAlerts/useAppsAlert";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import { useExtensionPreferences } from "@dashboard/extensions/preferences/useExtensionPreferences";
import {
  extensionsAppSection,
  extensionsCustomSection,
  ExtensionsPaths,
  extensionsPluginSection,
} from "@dashboard/extensions/urls";
import { giftCardListUrl } from "@dashboard/giftCards/urls";
import { PermissionEnum } from "@dashboard/graphql";
import { HOMEPAGE_WIDGETS_MOUNT } from "@dashboard/home/filterHomeExtensions";
import { getPinnedHomeWidgetMenuItems } from "@dashboard/home/pinnedHomeWidgetMenuItems";
import { rippleHomeWidgets } from "@dashboard/home/ripples/homeWidgets";
import { ConfigurationIcon } from "@dashboard/icons/Configuration";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { DiscountsIcon } from "@dashboard/icons/Discounts";
import { HomeIcon } from "@dashboard/icons/Home";
import { MarketplaceIcon } from "@dashboard/icons/Marketplace";
import { ModelingIcon } from "@dashboard/icons/Modeling";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import { TranslationsIcon } from "@dashboard/icons/Translations";
import { commonMessages, sectionNames } from "@dashboard/intl";
import { pageListPath } from "@dashboard/modeling/urls";
import { pageTypeListUrl } from "@dashboard/modelTypes/urls";
import { messages as navigationPinMessages } from "@dashboard/navigationPins/constants";
import { useResolvedNavigationPins } from "@dashboard/navigationPins/hooks/useResolvedNavigationPins";
import { injectNavigationPins } from "@dashboard/navigationPins/injectNavigationPins";
import { orderDraftListUrl, orderListUrl } from "@dashboard/orders/urls";
import { productListUrl } from "@dashboard/products/urls";
import { productTypeListUrl } from "@dashboard/productTypes/urls";
import { Ripple } from "@dashboard/ripples/components/Ripple";
import { SearchShortcut } from "@dashboard/search/SearchShortcut";
import { menuListUrl } from "@dashboard/structures/urls";
import { languageListUrl } from "@dashboard/translations/urls";
import { Box, Text } from "@saleor/macaw-ui-next";
import isEmpty from "lodash/isEmpty";
import { ScrollText, Search, Star, Warehouse } from "lucide-react";
import { useIntl } from "react-intl";

import { SidebarIconSlot } from "../../SidebarIconSlot";
import { createSettingsSubmenuItem } from "../settingsSubmenuItem";
import { type SidebarMenuItem } from "../types";
import { mapToExtensionsItems } from "../utils";
import { useCustomerTypeMenuItems } from "./useCustomerTypeMenuItems";

export function useMenuStructure() {
  const { handleAppsListItemClick, hasProblems } = useAppsAlert();
  const { trackEvent } = useAnalytics();

  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const intl = useIntl();
  const { user } = useUser();
  const navigationPins = useResolvedNavigationPins();
  const homeExtensions = useExtensions(HOMEPAGE_WIDGETS_MOUNT).HOMEPAGE_WIDGETS;
  const { getState } = useExtensionPreferences();
  const pinnedHomeWidgets = getPinnedHomeWidgetMenuItems(
    homeExtensions,
    user?.userPermissions ?? [],
    getState,
  ).map(item => ({
    ...item,
    onClick: () => {
      const extension = homeExtensions.find(candidate => `home-widget-${candidate.id}` === item.id);

      trackEvent("home_widget_opened", {
        extension_origin:
          extension === undefined
            ? undefined
            : extension.isSaleorOfficial
              ? "saleor"
              : "third_party",
        source: "sidebar",
        widget_kind: "fullscreen",
      });
    },
  }));
  const customerTypeMenuItems = useCustomerTypeMenuItems();

  const appExtensionsHeaderItem: SidebarMenuItem = {
    id: "extensions",
    label: intl.formatMessage(sectionNames.appExtensions),
    type: "divider",
    paddingY: 1.5,
  };

  const getExtensionsSection = (): SidebarMenuItem => ({
    icon: renderIcon(<MarketplaceIcon />),
    label: intl.formatMessage(sectionNames.extensions),
    permissions: [],
    id: "installed-extensions",
    url: ExtensionsPaths.installedExtensions,
    type: "itemGroup",
    endAdornment: <SidebarAppAlert hasNewFailedAttempts={hasProblems} />,
    onClick: () => handleAppsListItemClick(new Date().toISOString()),
    children: [
      {
        label: (
          <Box display="flex" alignItems="center" gap={3}>
            <Text size={3} fontWeight="medium">
              {intl.formatMessage(sectionNames.installedExtensions)}
            </Text>
            <SidebarAppAlert hasNewFailedAttempts={hasProblems} small />
          </Box>
        ),
        id: "installed-extensions",
        url: ExtensionsPaths.installedExtensions,
        matchUrls: [
          ExtensionsPaths.installedExtensions,
          extensionsCustomSection,
          extensionsAppSection,
          extensionsPluginSection,
        ],
        permissions: [],
        type: "item",
      },
      {
        label: intl.formatMessage(sectionNames.exploreExtensions),
        id: "explore-extensions",
        url: ExtensionsPaths.exploreExtensions,
        permissions: [],
        type: "item" as const,
      },
    ],
  });

  const menuItems: SidebarMenuItem[] = [
    {
      icon: renderIcon(<HomeIcon />),
      label: intl.formatMessage(sectionNames.home),
      id: "home",
      url: "/home",
      type: pinnedHomeWidgets.length > 0 ? "itemGroup" : "item",
      children: pinnedHomeWidgets.length > 0 ? pinnedHomeWidgets : undefined,
      // A pinned widget hidden behind a collapsed group defeats the point of pinning.
      defaultExpanded: true,
      endAdornment: <Ripple model={rippleHomeWidgets} />,
    },
    {
      icon: renderIcon(<Search {...navigationLucideIconProps} />),
      label: (
        <Box display="flex" alignItems="center" gap={2}>
          <Text size={3} fontWeight="medium">
            {intl.formatMessage(sectionNames.search)}
          </Text>
          <SearchShortcut />
        </Box>
      ),
      id: "search",
      url: "/search",
      permissions: [
        PermissionEnum.MANAGE_PRODUCTS,
        PermissionEnum.MANAGE_PAGES,
        PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
        PermissionEnum.MANAGE_ORDERS,
      ],
      type: "item",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.products),
          id: "products",
          url: productListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.categories),
          id: "categories",
          url: categoryListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.collections),
          id: "collections",
          url: collectionListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.giftCards),
          id: "giftCards",
          url: giftCardListUrl(),
          permissions: [PermissionEnum.MANAGE_GIFT_CARD],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CATALOG, appExtensionsHeaderItem),
        createSettingsSubmenuItem({
          id: "product-types",
          label: intl.formatMessage(sectionNames.productTypes),
          url: productTypeListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES],
        }),
      ],
      icon: renderIcon(<ProductsIcon />),
      url: productListUrl(),
      label: intl.formatMessage(sectionNames.catalog),
      permissions: [
        PermissionEnum.MANAGE_GIFT_CARD,
        PermissionEnum.MANAGE_PRODUCTS,
        PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
      ],
      id: "products",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.orders),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "orders",
          url: orderListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.draftOrders),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "order-drafts",
          url: orderDraftListUrl(),
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_ORDERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<OrdersIcon />),
      label: intl.formatMessage(sectionNames.fulfillment),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "orders",
      url: orderListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        ...customerTypeMenuItems,
        ...mapToExtensionsItems(extensions.NAVIGATION_CUSTOMERS, appExtensionsHeaderItem),
        createSettingsSubmenuItem({
          id: "customer-types",
          label: intl.formatMessage(sectionNames.customerTypes),
          url: customerTypeListUrl(),
          permissions: [PermissionEnum.MANAGE_CUSTOMER_TYPES_AND_ATTRIBUTES],
        }),
      ],
      icon: renderIcon(<CustomersIcon />),
      label: intl.formatMessage(sectionNames.customers),
      // Sidebar gating uses any-of matching, so users with only MANAGE_ORDERS
      // or MANAGE_STAFF can navigate to customer pages in read-only mode while
      // edit affordances remain hidden inside the section itself.
      permissions: [
        PermissionEnum.MANAGE_USERS,
        PermissionEnum.MANAGE_ORDERS,
        PermissionEnum.MANAGE_STAFF,
        PermissionEnum.MANAGE_CUSTOMER_TYPES_AND_ATTRIBUTES,
      ],
      endAdornment: <Ripple model={rippleNewCustomersView} />,
      id: "customers",
      url: customerListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.promotions),
          id: "promotions",
          url: saleListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.vouchers),
          id: "vouchers",
          url: voucherListUrl(),
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_DISCOUNTS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<DiscountsIcon />),
      label: intl.formatMessage(commonMessages.discounts),
      permissions: [PermissionEnum.MANAGE_DISCOUNTS],
      url: saleListUrl(),
      id: "discounts",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.models),
          id: "models",
          url: pageListPath,
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.structures),
          id: "structures",
          url: menuListUrl(),
          permissions: [PermissionEnum.MANAGE_MENUS],
          type: "item",
        },
        // TCG Attic: Digital Assets (attic/assets), see ATTIC.md "Attic sections"
        {
          label: intl.formatMessage(sectionNames.atticDigitalAssets),
          id: "attic-digital-assets",
          url: atticAssetDirectoryListPath,
          permissions: [PermissionEnum.MANAGE_ASSETS],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_PAGES, appExtensionsHeaderItem),
        createSettingsSubmenuItem({
          id: "model-types",
          label: intl.formatMessage(sectionNames.modelTypes),
          url: pageTypeListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
        }),
      ],
      icon: renderIcon(<ModelingIcon />),
      label: intl.formatMessage(sectionNames.modeling),
      permissions: [
        PermissionEnum.MANAGE_PAGES,
        PermissionEnum.MANAGE_MENUS,
        PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
      ],
      id: "modeling",
      url: pageListPath,
      type: "itemGroup",
    },
    {
      children: !isEmpty(extensions.NAVIGATION_TRANSLATIONS)
        ? [...mapToExtensionsItems(extensions.NAVIGATION_TRANSLATIONS, appExtensionsHeaderItem)]
        : undefined,
      icon: renderIcon(<TranslationsIcon />),
      label: intl.formatMessage(sectionNames.translations),
      permissions: [PermissionEnum.MANAGE_TRANSLATIONS],
      id: "translations",
      url: languageListUrl,
      type: !isEmpty(extensions.NAVIGATION_TRANSLATIONS) ? "itemGroup" : "item",
    },
    getExtensionsSection(),
    // TCG Attic: Inventory (attic/inventory), see ATTIC.md "Attic sections"
    {
      icon: renderIcon(<Warehouse {...navigationLucideIconProps} />),
      label: intl.formatMessage(sectionNames.atticInventory),
      permissions: [PermissionEnum.VIEW_INVENTORY],
      id: "attic-inventory",
      url: atticStockLookupPath,
      type: "item",
    },
    // TCG Attic: Activity Log (attic/activity), see ATTIC.md "Attic sections"
    {
      icon: renderIcon(<ScrollText {...navigationLucideIconProps} />),
      label: intl.formatMessage(sectionNames.atticActivityLog),
      permissions: [PermissionEnum.VIEW_ACTIVITY_LOG],
      id: "attic-activity-log",
      url: atticActivityListPath,
      type: "item",
    },
    {
      icon: renderIcon(<ConfigurationIcon />),
      label: intl.formatMessage(sectionNames.configuration),
      permissions: getConfigMenuItemsPermissions(intl),
      id: "configure",
      url: configurationMenuUrl,
      type: "item",
    },
  ];
  // Pins are injected before filtering, so one whose host section is hidden drops out with it.
  const menuItemsWithPins = injectNavigationPins(menuItems, navigationPins, {
    favoritesLabel: intl.formatMessage(navigationPinMessages.favorites),
    favoritesIcon: renderIcon(<Star {...navigationLucideIconProps} />),
  });
  const isMenuItemPermitted = (menuItem: SidebarMenuItem) => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };
  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  return menuItemsWithPins.reduce((resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems;
    }

    const { children } = menuItem;
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

    return [...resultItems, { ...menuItem, children: filteredChildren }];
  }, []);
}

function renderIcon(icon: React.ReactNode) {
  return <SidebarIconSlot>{icon}</SidebarIconSlot>;
}
